<template>
    <RegularModal ref="modal">
        <template v-slot:title> {{ translate("admin_schedule_manage.distribute_modal_title") }} </template>
        <template v-slot:body>
            <div>
                <p class="mb-1">{{ translate("admin_schedule_manage.select_mats") }}</p>
                <SelectionList
                    :options="availableMats"
                    v-model="selectedMats"
                    :multi-select="true"
                    :comparing-function="(a: Mat, b: Mat) => a.id === b.id"
                >
                    <template v-slot:option="{ option }">
                        {{ option.name }}
                    </template>
                </SelectionList>
            </div>

            <div class="mt-3">
                <p class="mb-1">{{ translate("admin_schedule_manage.order_of_distributing") }}</p>
                <Radio v-model="distributingOrder" :initial-value="'selection'" name="sorting">
                    {{ translate("common.as_marked") }}
                </Radio>
                <Radio v-model="distributingOrder" :initial-value="'desc'" name="sorting">
                    {{ translate("common.desc") }}
                </Radio>
                <Radio v-model="distributingOrder" :initial-value="'asc'" name="sorting">
                    {{ translate("common.asc") }}
                </Radio>
            </div>
        </template>
        <template v-slot:footer>
            <button class="button" @click="close()">{{ translate("common.close") }}</button>
            <LoadingButton
                :is-loading="distributeInProgress"
                class="button is-primary"
                :disabled="!distributeEnabled"
                @click="distribute()"
            >
                {{ translate("admin_schedule_manage.distribute") }}
            </LoadingButton>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import { computed, ref, triggerRef } from "vue";
    import SelectionList from "../../Common/SelectionList.vue";
    import Radio from "../../Common/Radio.vue";
    import mats from "../../Api/mats";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { Mat } from "../../Common/types";
    import schedulesApi, { AddingOrder } from "../../Api/schedules";
    import { handleError } from "../../Common/errorHandler";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import { successToast } from "../../Common/toasts";
    import { translate } from "../../Common/translations";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import type { BulkSaveGroup, DropZoneItem } from "./dropZoneItems";
    import { groupSlotKey } from "./dropZoneItems";

    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    const initDone = ref(false);

    const { modal, open: openModal, close } = useModal();

    function open() {
        initDone.value = false;
        selectedMats.value = [];
        distributingOrder.value = "selection";
        availableMats.value = [];

        triggerRef(distributingOrder);

        openModal();

        return mats
            .getMatsListing(mainStore.eventId)
            .then(result => {
                availableMats.value = result;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    defineExpose({ open });

    // Mat selector

    const selectedMats = ref<Mat[]>([]);

    const availableMats = ref<Mat[]>([]);

    // Order of distributing, default here is desc, despite selection shoyld be default. It's about triggering reactivity.
    const distributingOrder = ref<AddingOrder>("desc");

    // Distributing action

    const distributeInProgress = ref(false);

    const distributeEnabled = computed(() => {
        return (
            selectedMats.value.length > 0 && distributingOrder.value !== null && showScheduleStore.findSelectedDropZoneItems().length > 0
        );
    });

    function distribute() {
        const selectedDropZoneItems = showScheduleStore.findSelectedDropZoneItems();
        const groupedItems = groupDropZoneItemsForBulkSave(selectedDropZoneItems);

        distributeInProgress.value = true;
        return groupedItems
            .reduce((promise, group) => {
                return promise.then(() =>
                    mainStore.trackRequest(
                        schedulesApi.bulkSaveScheduledCategories({
                            eventId: mainStore.eventId,
                            scheduleId: mainStore.scheduleId,
                            matIds: selectedMats.value.map(m => m.id),
                            parameterIds: group.items.map(item => item.parameterId),
                            assignmentType: group.assignmentType,
                            addingOrder: distributingOrder.value,
                            roundMarkerFrom: group.roundMarkerFrom,
                            roundMarkerTo: group.roundMarkerTo,
                        }),
                    ),
                );
            }, Promise.resolve())
            .then(() => {
                showScheduleStore.clearSelection();
                return Promise.all([
                    showScheduleStore.refreshSingleMatSchedule(selectedMats.value.map(m => m.id)),
                    mainStore.refreshDropZoneItems(),
                ]);
            })
            .then(() => {
                successToast(translate("admin_schedule_manage.categories_distributed_successfully"));
                close();
            })
            .catch(handleError)
            .finally(() => {
                distributeInProgress.value = false;
            });
    }

    function groupDropZoneItemsForBulkSave(items: DropZoneItem[]) {
        const groups = new Map<string, BulkSaveGroup>();

        items.forEach(item => {
            const key = groupSlotKey(item);
            const group = groups.get(key);

            if (group) {
                group.items.push(item);
                return;
            }

            groups.set(key, {
                assignmentType: item.assignmentType,
                roundMarkerFrom: item.roundMarkerFrom,
                roundMarkerTo: item.roundMarkerTo,
                items: [item],
            });
        });

        return Array.from(groups.values());
    }
</script>

<style scoped></style>
